import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, FilterIcon, TagIcon } from 'lucide-react';
import { TagType } from './TagsManagement';
interface TagTypesListProps {
  tagTypes: TagType[];
  onAdd: () => void;
  onEdit: (tagType: TagType) => void;
  onDelete: (id: string) => void;
  locale: string;
}
export function TagTypesList({
  tagTypes,
  onAdd,
  onEdit,
  onDelete,
  locale
}: TagTypesListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  // Apply search filter
  const filteredTagTypes = tagTypes.filter(tagType => {
    return tagType.name.toLowerCase().includes(searchTerm.toLowerCase()) || tagType.description && tagType.description.toLowerCase().includes(searchTerm.toLowerCase());
  });
  // Render color palette
  const renderColorPalette = (colors: string[] | null) => {
    if (!colors || colors.length === 0) {
      return <span className="text-gray-400 italic" data-id="element-17967">No color palette defined</span>;
    }
    return <div className="flex space-x-1" data-id="element-17968">
        {colors.map((color, index) => <div key={index} className="h-4 w-4 rounded-full" style={{
        backgroundColor: color
      }} title={color} data-id="element-17969" />)}
      </div>;
  };
  // Render tag type card
  const renderTagTypeCard = (tagType: TagType) => {
    return <div className="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200" data-id="element-17970">
        <div className="px-4 py-5 sm:px-6" data-id="element-17971">
          <div className="flex justify-between items-start" data-id="element-17972">
            <div data-id="element-17973">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-17974">
                <TagIcon className="h-5 w-5 mr-2 text-indigo-600" data-id="element-17975" />
                {tagType.name}
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-17976">
                {tagType.description || <span className="text-gray-400 italic" data-id="element-17977">No description</span>}
              </p>
            </div>
            <div className="flex space-x-2" data-id="element-17978">
              <button onClick={() => onEdit(tagType)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-17979">
                <EditIcon className="h-4 w-4" data-id="element-17980" />
              </button>
              <button onClick={() => onDelete(tagType.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-17981">
                <TrashIcon className="h-4 w-4" data-id="element-17982" />
              </button>
            </div>
          </div>
        </div>
        <div className="px-4 py-4 sm:px-6" data-id="element-17983">
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2" data-id="element-17984">
            <div data-id="element-17985">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17986">
                Restricted
              </span>
              <p className="mt-1 text-sm text-gray-900" data-id="element-17987">
                {tagType.isRestricted ? 'Yes' : 'No'}
              </p>
              <p className="mt-1 text-xs text-gray-500" data-id="element-17988">
                {tagType.isRestricted ? 'Only predefined tag values are allowed' : 'Users can create any tag value'}
              </p>
            </div>
            <div data-id="element-17989">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17990">
                Color Palette
              </span>
              <div className="mt-1" data-id="element-17991">
                {renderColorPalette(tagType.colourPalette)}
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="space-y-6" data-id="element-17992">
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-17993">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-17994">
          <div className="flex-1 max-w-md" data-id="element-17995">
            <div className="relative rounded-md shadow-sm" data-id="element-17996">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-17997">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-17998" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search tag types..." data-id="element-17999" />
            </div>
          </div>
          <button onClick={onAdd} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18000">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-18001" />
            Add Tag Type
          </button>
        </div>
      </div>
      {filteredTagTypes.length === 0 ? <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-18002">
          <div className="px-4 py-5 sm:p-6 text-center" data-id="element-18003">
            <p className="text-sm text-gray-500" data-id="element-18004">
              No tag types found. Click "Add Tag Type" to create one.
            </p>
          </div>
        </div> : <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-18005">
          {filteredTagTypes.map(tagType => <div key={tagType.id} data-id="element-18006">{renderTagTypeCard(tagType)}</div>)}
        </div>}
    </div>;
}